<template>
  <Pane :value="filtersPaneIsOpen"
        :tabs="tabs"
        location="left"
        current-tab="filters"
        temporary
        class="ChecklistFiltersPane"
        data-locator="checklist-filters-pane"
        @update:modelValue="onPaneValueChanged"
  >
    <template #content-filters>
      <template v-if="filtersPaneIsOpen">
        <div class="d-flex flex-column row-gap-4">
          <div>
            <div class="font-weight-bold mb-2">{{ $t(`${TRANSLATION_KEY_PREFIX}.completionDateFilterTitle`) }}</div>
            <AppDateRangePicker v-model="localFilters.completionDate" multiple label="" />
          </div>
          
          <v-divider />
          
          <template v-if="checklistResponsibles.length > 0">
            <div>
              <div class="font-weight-bold mb-2">{{ $t(`${TRANSLATION_KEY_PREFIX}.responsiblePartiesFilterTitle`) }}</div>
              <AppTextField v-if="checklistResponsibles.length >= 10"
                            v-model.trim="responsiblePartiesSearchQuery"
                            :label="$t(`${TRANSLATION_KEY_PREFIX}.searchResponsibleParty`)"
                            prepend-inner-icon="search"
                            style="max-width: 100%"
                            class="ChecklistFiltersPane-searchInput"
              />
              <v-checkbox v-for="responsible in responsiblesToDisplay"
                          :key="`responsible-${responsible.key}`"
                          :label="responsible.fullName || responsible.name"
                          :value="responsible"
                          density="compact"
                          hide-details
                          v-model="localFilters.responsibleParties"
              />
              <div v-if="filteredChecklistResponsibles.length > maxResponsiblesToDisplay" class="mt-3">
                <button class="font-weight-medium text-decoration-underline text-black"
                        @click="maxResponsiblesToDisplay += 10">
                  {{$t(`${TRANSLATION_KEY_PREFIX}.seeMoreResponsibles`)}}
                </button>
              </div>
            </div>

            <v-divider />
          </template>

          <template v-if="checklistValidators.length > 0">
            <div class="d-flex flex-column">
              <div class="font-weight-bold mb-2">{{ $t(`${TRANSLATION_KEY_PREFIX}.validatorsFilterTitle`) }}</div>
              <AppTextField v-if="checklistValidators.length >= 10"
                            v-model.trim="validatorsSearchQuery"
                            :label="$t(`${TRANSLATION_KEY_PREFIX}.searchValidators`)"
                            prepend-inner-icon="search"
                            style="max-width: 100%"
                            class="ChecklistFiltersPane-searchInput"
              />
              <v-checkbox v-for="validator in validatorsToDisplay"
                          :key="`validators-${validator.key}`"
                          :label="validator.fullName || validator.name"
                          :value="validator"
                          density="compact"
                          hide-details
                          v-model="localFilters.validators"
              />
              <div v-if="filteredChecklistValidators.length > maxValidatorsToDisplay" class="mt-3">
                <button class="font-weight-medium text-decoration-underline text-black"
                        @click="maxValidatorsToDisplay += 10">
                  {{$t(`${TRANSLATION_KEY_PREFIX}.seeMoreValidators`)}}
                </button>
              </div>
            </div>
            
            <v-divider />
          </template>

          <template v-if="checklistStatuses.length > 0">
            <div>
              <div class="font-weight-bold mb-2">{{ $t(`${TRANSLATION_KEY_PREFIX}.statusesFilterTitle`) }}</div>
              <v-checkbox v-for="status in checklistStatuses"
                          :key="`status-${status.id}`"
                          :label="status.label"
                          :value="status.id"
                          density="compact"
                          hide-details
                          v-model="localFilters.statuses"
              />
            </div>
          </template>

          <div class="ChecklistFiltersPane-stickyButton-bar pt-4">
            <app-button
              type="outlined"
              variant="neutral"
              data-locator="clear-button"
              @click="onClearAllFilters"
            >
              {{ $t(`${TRANSLATION_KEY_PREFIX}.resetFiltersButton`) }}
            </app-button>
            <app-button data-locator="apply-button" @click="onApplyFilters">
              {{ $t(`${TRANSLATION_KEY_PREFIX}.applyFiltersButton`) }}
            </app-button>
          </div>
        </div>
      </template>
      
      <AppDialog :is-open="openCloseDialog"
                 :ok-text="$t('common.yes')"
                 :cancel-text="$t('common.no')"
                 @ok="onOkCloseDialog"
                 @cancel="onCancelCloseDialog"
      >
        <template #title>{{ $t(`${TRANSLATION_KEY_PREFIX}.closeDialogTitle`) }}</template>
        <template #body>
          {{ $t(`${TRANSLATION_KEY_PREFIX}.closeDialogText`) }}
        </template>
      </AppDialog>
    </template>
  </Pane>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import Pane from '@/common/Pane.vue'
import { getStatusDetailsByTaskInformation } from '@/common/utils/checklist'
import { formatDateValue } from '@/common/utils/dates'
import { Filter } from '@/models/filter.model'
import {
  SET_CHECKLIST_FILTERS,
  SET_FILTERS_PANE_IS_OPEN,
  SET_SELECTED_TASKS,
  SET_SELECTED_SECTIONS,
} from '@/store/modules/checklist/mutation_types'

const EMPTY_FILTERS = {
  completionDate: [],
  responsibleParties: [],
  statuses: [],
  validators: [],
}

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistFiltersPane'

export default {
  name: 'ChecklistFiltersPane',
  components: { AppDialog, AppDateRangePicker, AppTextField, Pane },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      localFilters: { ...EMPTY_FILTERS },
      maxResponsiblesToDisplay: 10,
      maxValidatorsToDisplay: 10,
      openCloseDialog: false,
      responsiblePartiesSearchQuery: '',
      tabs: [{ id: 'filters', title: this.$t('common.filters') }],
      validatorsSearchQuery: '',
    }
  },
  computed: {
    ...mapState('checklist', ['filters', 'filtersPaneIsOpen', 'taskStatusCategories']),
    ...mapGetters('checklist', ['tasks']),
    filtersHasChange () {
      return this.localFilters?.completionDate.length !== this.filters.filter(filter => filter.category.key === 'completionDate').length ||
        this.localFilters?.responsibleParties.length !== this.filters.filter(filter => filter.category.key === 'responsibleParties').length ||
        this.localFilters?.validators.length !== this.filters.filter(filter => filter.category.key === 'validators').length ||
        this.localFilters?.statuses.length !== this.filters.filter(filter => filter.category.key === 'statuses').length
    },
    checklistResponsibles () {
      const responsibles = this.tasks.reduce((acc, task) => {
        if (task.rights) {
          task.rights.responsibles.groups.forEach(group => {
            if (!acc.some(el => el.id === group.id)) {
              acc.push({ ...group, key: `group-${group.id}` })
            }
          })
          task.rights.responsibles.users.forEach(user => {
            if (!acc.some(el => el.id === user.id)) {
              acc.push({ ...user, key: `user-${user.id}` })
            }
          })
        }
        return acc
      }, [])

      return responsibles
    },
    sortedChecklistResponsibles () {
      return [...this.checklistResponsibles].sort((a, b) => {
        if (a.fullName && b.fullName) {
          return a.fullName.localeCompare(b.fullName)
        } else if (a.fullName) {
          return a.fullName.localeCompare(b.name)
        } else if (b.fullName) {
          return a.name.localeCompare(b.fullName)
        } else {
          return a.name.localeCompare(b.name)
        }
      })
    },
    filteredChecklistResponsibles () {
      if (!this.responsiblePartiesSearchQuery) {
        return this.sortedChecklistResponsibles
      }

      return this.sortedChecklistResponsibles.filter(resp => {
        if (resp.fullName) {
          return resp.fullName.toLowerCase().includes(this.responsiblePartiesSearchQuery.toLowerCase())
        } else if (resp.name) {
          return resp.name.toLowerCase().includes(this.responsiblePartiesSearchQuery.toLowerCase())
        }
      })
    },
    responsiblesToDisplay () {
      return this.filteredChecklistResponsibles.slice(0, this.maxResponsiblesToDisplay)
    },
    checklistValidators () {
      const validators = this.tasks.reduce((acc, task) => {
        if (task.rights) {
          task.rights.validators.groups.forEach(group => {
            if (!acc.some(el => el.id === group.id)) {
              acc.push({ ...group, key: `group-${group.id}` })
            }
          })
          task.rights.validators.users.forEach(user => {
            if (!acc.some(el => el.id === user.id)) {
              acc.push({ ...user, key: `user-${user.id}` })
            }
          })
        }
        return acc
      }, [])

      return validators
    },
    sortedChecklistValidators () {
      return [...this.checklistValidators].sort((a, b) => {
        if (a.fullName && b.fullName) {
          return a.fullName.localeCompare(b.fullName)
        } else if (a.fullName) {
          return a.fullName.localeCompare(b.name)
        } else if (b.fullName) {
          return a.name.localeCompare(b.fullName)
        } else {
          return a.name.localeCompare(b.name)
        }
      })
    },
    filteredChecklistValidators () {
      if (!this.validatorsSearchQuery) {
        return this.sortedChecklistValidators
      }

      return this.sortedChecklistValidators.filter(val => {
        if (val.fullName) {
          return val.fullName.toLowerCase().includes(this.validatorsSearchQuery.toLowerCase())
        } else if (val.name) {
          return val.name.toLowerCase().includes(this.validatorsSearchQuery.toLowerCase())
        }
      })
    },
    validatorsToDisplay () {
      return this.filteredChecklistValidators.slice(0, this.maxValidatorsToDisplay)
    },
    checklistStatuses () {
      const statuses = []
      this.tasks.forEach(task => {
        const statusDetails = getStatusDetailsByTaskInformation(
          task.status.id,
          task.status.category,
          this.taskStatusCategories,
        )

        if (statusDetails && !statuses.find((status) => status.id === task.status.id)) {
          statuses.push(statusDetails)
        }
      })

      return statuses
    },
  },
  watch: {
    filtersPaneIsOpen (value) {
      if (value) {
        this.syncAppliedFiltersWithLocals()
      }
    },
  },
  methods: {
    ...mapMutations('checklist', [
      SET_CHECKLIST_FILTERS,
      SET_FILTERS_PANE_IS_OPEN,
      SET_SELECTED_TASKS,
      SET_SELECTED_SECTIONS,
    ]),
    applyFilters () {
      const completionDateFilters = this.localFilters.completionDate.length > 0
        ? [
          new Filter(
            'completionDate',
            this.$t(`${TRANSLATION_KEY_PREFIX}.completionDateFilterTitle`),
            this.localFilters.completionDate,
            formatDateValue(this.localFilters.completionDate),
          )]
        : []
      const responsiblePartiesFilters = this.localFilters.responsibleParties.map(responsibleParty =>
        new Filter('responsibleParties', this.$t(`${TRANSLATION_KEY_PREFIX}.responsiblePartiesFilterTitle`), responsibleParty.key, (responsibleParty?.fullName || responsibleParty?.name)),
      )
      const statusesFilters = this.localFilters.statuses.map(statusId =>
        new Filter('statuses', this.$t(`${TRANSLATION_KEY_PREFIX}.statusesFilterTitle`), statusId, this.checklistStatuses.find(status => status.id === statusId).label),
      )
      const validatorsFilters = this.localFilters.validators.map(validator =>
        new Filter('validators', this.$t(`${TRANSLATION_KEY_PREFIX}.validatorsFilterTitle`), validator.key, (validator?.fullName || validator?.name)),
      )

      const filters = [
        ...completionDateFilters,
        ...responsiblePartiesFilters,
        ...statusesFilters,
        ...validatorsFilters,
      ]

      this.SET_CHECKLIST_FILTERS(filters)
      this.SET_SELECTED_TASKS([])
      this.SET_SELECTED_SECTIONS([])
    },
    closePane () {
      this.SET_FILTERS_PANE_IS_OPEN(false)
    },
    onApplyFilters () {
      this.applyFilters()
      this.closePane()
    },
    onCancelCloseDialog () {
      this.openCloseDialog = false
      this.closePane()
    },
    onClearAllFilters () {
      this.localFilters = { ...EMPTY_FILTERS }
      this.applyFilters()
    },
    onOkCloseDialog () {
      this.openCloseDialog = false
      this.applyFilters()
      this.closePane()
    },
    onPaneValueChanged (isOpen) {
      if (!isOpen && this.filtersPaneIsOpen && this.filtersHasChange) {
        this.openCloseDialog = true
      } else if (!isOpen && this.filtersPaneIsOpen) {
        this.closePane()
      }
    },
    syncAppliedFiltersWithLocals () {
      const datesFilters = this.filters.find(filter => filter.category.key === 'completionDate')
      this.localFilters.completionDate = datesFilters?.value.key ?? []

      this.localFilters.responsibleParties = this.filters
        .filter(filter => filter.category.key === 'responsibleParties')
        .map(filter => this.checklistResponsibles.find(responsible => responsible.key === filter.value.key))
      this.localFilters.statuses = this.filters
        .filter(filter => filter.category.key === 'statuses')
        .map(filter => filter.value.key)
      this.localFilters.validators = this.filters
        .filter(filter => filter.category.key === 'validators')
        .map(filter => this.checklistValidators.find(validator => validator.key === filter.value.key))
    },
  },
}
</script>

<style scoped lang="scss">
  :deep(.ChecklistFiltersPane-searchInput .v-icon) {
    font-size: 16px;
  }
  .ChecklistFiltersPane-stickyButton-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: space-between;
  }

  :deep(.ChecklistFiltersPane .pane-content) {
    padding-bottom: 0;
    overflow-y: auto;
  }
</style>
